import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Users, ChevronRight, Download, GraduationCap, Briefcase, Video, Globe } from 'lucide-react';
import { useCasePages } from '../data/knowledgeHub';

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.typeit4u.keyboard";

const iconMap = {
  'for-students': GraduationCap,
  'for-business': Briefcase,
  'for-content-creators': Video,
  'for-tamil-users': Globe
};

const UseCases = () => {
  const springConfig = {
    type: "spring",
    damping: 80,
    stiffness: 400,
    mass: 1
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-orange-50 via-white to-pink-50">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
            className="max-w-3xl mx-auto text-center"
          >
            <Users className="w-16 h-16 mx-auto mb-6" style={{ color: '#FF6B9D' }} />
            <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ color: '#0A0A0A' }}>
              TypeIt4U for Everyone
            </h1>
            <p className="text-base sm:text-lg text-gray-600 mb-8">
              Discover how TypeIt4U helps different users type smarter and faster
            </p>
          </motion.div>
        </div>
      </section>

      {/* Use Case Cards */}
      <section className="py-16 bg-white">
        <div className="container max-w-5xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {useCasePages.map((useCase, index) => {
              const Icon = iconMap[useCase.id] || Users;
              return (
                <motion.div
                  key={useCase.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...springConfig, delay: index * 0.1 }}
                >
                  <Link
                    to={`/use-case/${useCase.slug}`}
                    className="block bg-gradient-to-br from-orange-50 to-pink-50 rounded-2xl p-8 border border-orange-100 hover:shadow-xl transition-all h-full"
                  >
                    <div 
                      className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
                      style={{ background: 'linear-gradient(135deg, #FF8C42 0%, #FF6B9D 100%)' }}
                    >
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    
                    <h3 className="text-2xl font-bold mb-3" style={{ color: '#0A0A0A' }}>
                      TypeIt4U for {useCase.audience}
                    </h3>
                    <p className="text-gray-600 mb-6">
                      {useCase.description}
                    </p>
                    
                    {/* Preview benefits */}
                    <ul className="space-y-2 mb-6">
                      {useCase.benefits.slice(0, 3).map((benefit, i) => (
                        <li key={i} className="text-sm text-gray-700 flex items-center gap-2">
                          <span style={{ color: '#FF6B9D' }}>✓</span>
                          {benefit}
                        </li>
                      ))}
                    </ul>
                    
                    <span className="inline-flex items-center gap-1 text-orange-600 font-medium">
                      Learn more <ChevronRight className="w-4 h-4" />
                    </span>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-orange-50 to-pink-50">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="max-w-2xl mx-auto text-center"
          >
            <h3 className="text-2xl font-bold mb-4" style={{ color: '#0A0A0A' }}>
              Find Your Perfect Keyboard
            </h3>
            <p className="text-gray-600 mb-6">
              No matter who you are, TypeIt4U has features tailored for you.
            </p>
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2"
              style={{
                background: 'linear-gradient(135deg, #FF8C42 0%, #FF6B9D 100%)',
                color: 'white',
                borderRadius: '26px',
                padding: '16px 32px',
                fontSize: '16px',
                fontWeight: 500
              }}
            >
              <Download className="w-5 h-5" />
              Download TypeIt4U Free
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default UseCases;
